import React from "react";
import { fetchDomainConfig } from "../services/domainService";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { OrganizationPublicConfig } from "@/types/types";
import LoadingScreen from "./LoadingScreen";

interface DomainConfigProviderProps {
  children: React.ReactNode;
}

interface DomainConfigContextType {
  domainConfig: OrganizationPublicConfig | null | undefined;
  isLoading: boolean;
  error: Error | null;
}

const DomainConfigContext = React.createContext<DomainConfigContextType>({
  domainConfig: null,
  isLoading: true,
  error: null,
});

export const useDomainConfig = () => {
  const context = React.useContext(DomainConfigContext);
  if (context === undefined) {
    throw new Error("useDomainConfig must be used within a DomainConfigProvider");
  }
  return context;
};

/**
 * Provider that fetches and provides domain configuration on app startup
 * This enables multi-tenant support by getting domain-specific settings
 */
export const DomainConfigProvider: React.FC<DomainConfigProviderProps> = ({ children }) => {
  const { data, isPending, error } = useQuery({
    queryKey: ["domainConfig"],
    queryFn: fetchDomainConfig,
  });

  let errorToDisplay: string | null = null;

  const value: DomainConfigContextType = {
    domainConfig: data?.data,
    isLoading: isPending,
    error,
  };

  if (isPending) {
    return <LoadingScreen message="Loading domain configuration..." />;
  }

  if (data?.hasError) {
    errorToDisplay =
      data.validation.items.map((item) => item.message).join(", ") || "Unknown validation error";
  } else if (error) {
    errorToDisplay = error instanceof AxiosError ? error.message : "Unknown error";
  }

  if (error || !data?.data) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-neutral-500">
        <h1 className="text-2xl font-bold text-danger">Configuration Error</h1>
        <div className="text-base text-danger">Error: {errorToDisplay}</div>
        <div className="text-xs text-neutral-300">Domain: {window.location.hostname}</div>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 rounded border-2 border-danger bg-transparent px-4 py-2 font-medium text-danger shadow-default transition-colors hover:bg-danger hover:text-white"
        >
          Retry
        </button>
      </div>
    );
  }

  return <DomainConfigContext.Provider value={value}>{children}</DomainConfigContext.Provider>;
};
